import type {
  QueueHoldReason,
  QueuedChatMessage,
} from '@/features/chat/chat.store';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';

/**
 * Whether this user has Steer, and whether a message sent during a reply waits in a queue.
 * The backend allows Steer for every internal user; `isInternalUser` is false for one who hides
 * internal tools, so for them Steer follows the flag as for a customer. Steering works from the
 * queue, so it turns queueing on too. A hook, so flags that load after mount re-render the composer
 */
export const useQueueFeatures = () => {
  const hasFeature = useFeature();
  const isInternalUser = useIsInternalUser();
  const isSteeringOn =
    isInternalUser || hasFeature(FEATURES.NON_INTERRUPTING_STEERING);
  return {
    isSteeringOn,
    canQueueMessages: isSteeringOn || hasFeature(FEATURES.QUEUED_MESSAGES),
  };
};

/**
 * What the first queued message's button does: `interrupt` stops the running response and sends,
 * `send` sends from a held queue while no response runs, `steer` hands it to the running
 * response, and `steer_unavailable` means it waits for the response to end
 */
export type SendNowAction =
  | 'interrupt'
  | 'send'
  | 'steer'
  | 'steer_unavailable';

/**
 * Steering adds only text, selected text and attachments to the running turn, so a message with
 * mentions or answers waits for the turn to end. Text that starts with a slash may be a command,
 * which only a normal send runs
 */
export const canJoinRunningTurn = (message: QueuedChatMessage): boolean =>
  (message.content.trim() !== '' ||
    message.selectedTextCount > 0 ||
    message.attachmentCount > 0) &&
  !message.content.trimStart().startsWith('/') &&
  message.mentions.length === 0 &&
  !message.askUserAnswers;

/**
 * What the first queued message's button and the send-now hotkey do; undefined while there is
 * nothing to send now. With nothing queued, Steer and Interrupt queue the composer's draft first
 */
export const getSendNowAction = ({
  isSteeringOn,
  isBusy,
  holdReason,
  nextQueuedMessage,
}: {
  isSteeringOn: boolean;
  isBusy: boolean;
  holdReason: QueueHoldReason | undefined;
  nextQueuedMessage: QueuedChatMessage | undefined;
}): SendNowAction | undefined => {
  // With no turn running and no hold, the queue sends by itself
  if (!isBusy && !holdReason) return undefined;
  if (isSteeringOn && isBusy) {
    return nextQueuedMessage && !canJoinRunningTurn(nextQueuedMessage)
      ? 'steer_unavailable'
      : 'steer';
  }
  // With steering off, the button stops a running turn first
  if (isBusy) return 'interrupt';
  // An unconfirmed stop may have left the turn running on the server, so it is stopped again
  return holdReason === 'stop_unacknowledged' ? 'interrupt' : 'send';
};
